import type { HidChannel } from '../HidChannel'
import type { HidDeviceFilter } from '../HidChannelDescriptor'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import type { HID, Device } from 'node-hid'

/**
 * NodeHID HID 通道实现
 * 使用 node-hid 进行通信，适用于 Node.js / Electron 主进程 / 开启了 nodeIntegration 的 Electron 渲染进程
 */
export class NodeHidChannel implements HidChannel {
  public nativeDevice: HID | null = null
  private path: string
  private _deviceInfo: { vendorId: number; productId: number; productName?: string } | null = null

  // 存储事件监听器的回调包装器映射
  private listeners: Map<(reportId: number, data: DataView) => void, (data: Buffer) => void> =
    new Map()

  constructor(pathOrDevice: string | Device) {
    if (typeof pathOrDevice === 'string') {
      this.path = pathOrDevice
    } else {
      this.path = pathOrDevice.path!
      this._deviceInfo = {
        vendorId: pathOrDevice.vendorId,
        productId: pathOrDevice.productId,
        productName: pathOrDevice.product,
      }
    }
  }

  /**
   * 检查当前环境是否支持 NodeHID
   */
  public static isSupported(): boolean {
    try {
      // 检查 Node.js 环境或 Electron 主进程
      const isNode = typeof process !== 'undefined' && process.versions && !!process.versions.node
      // 检查开启了 nodeIntegration 的 Electron 渲染进程 (window.require 可用)
      const isElectronRenderer = typeof window !== 'undefined' && !!(window as any).require

      return isNode || isElectronRenderer
    } catch (_error) {
      return false
    }
  }

  /**
   * 动态加载 node-hid 模块的辅助方法
   */
  private static loadNodeHid(): any {
    try {
      if (typeof window !== 'undefined' && (window as any).require) {
        return (window as any).require('node-hid')
      }
      if (typeof require !== 'undefined') {
        return require('node-hid')
      }
    } catch (_error) {
      console.error('加载 node-hid 失败:', _error)
    }
    throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, 'node-hid 不可用')
  }

  /**
   * 扫描设备
   */
  public static async scan(filters: HidDeviceFilter[]): Promise<Device[]> {
    if (!this.isSupported()) {
      return []
    }

    try {
      const HID = this.loadNodeHid()
      const devices: Device[] = HID.devices()

      // 调试日志：显示所有可用设备
      console.log(
        '[NodeHID] 所有检测到的设备:',
        devices.map((d) => ({
          path: d.path,
          vendorId: d.vendorId,
          productId: d.productId,
          usagePage: d.usagePage,
          usage: d.usage,
          interface: d.interface,
        })),
      )

      return devices.filter((d) => {
        return filters.some((f) => {
          const vidMatch = d.vendorId === f.vendorId
          const pidMatch = d.productId === f.productId

          // 如果过滤器指定了 usagePage/usage，则进行检查。
          // 在 macOS/Windows 上，这些信息通常是可用的。
          let usageMatch = true
          if (f.usagePage !== undefined) {
            if (d.usagePage !== undefined) {
              usageMatch = usageMatch && Number(d.usagePage) === Number(f.usagePage)
            } else {
              // 如果过滤器要求 usagePage 但设备未上报，
              // 视为不匹配，以避免连接到错误的接口。
              usageMatch = false
            }
          }

          if (f.usage !== undefined) {
            if (d.usage !== undefined) {
              usageMatch = usageMatch && d.usage === f.usage
            } else {
              usageMatch = false
            }
          }

          const match = vidMatch && pidMatch && usageMatch
          if (match) {
            console.log(
              `[NodeHID] 匹配设备: ${d.path} (UsagePage: ${d.usagePage}, Usage: ${d.usage})`,
            )
          }
          return match
        })
      })
    } catch (error) {
      // scan 失败通常不应抛出异常阻断流程，而是返回空数组，除非是关键错误
      // 这里如果 loadNodeHid 失败会抛出 DeviceError，scan 可以捕获它或者让它抛出
      // 为了稳健性，scan 失败返回空数组比较安全，但如果是 NOT_SUPPORTED 已经被处理了
      // 如果是其他错误，记录日志
      console.error('[NodeHID] 扫描设备失败:', error)
      return []
    }
  }

  public get isConnected(): boolean {
    return !!this.nativeDevice
  }

  public get deviceInfo() {
    return this._deviceInfo
  }

  public async open(): Promise<void> {
    if (this.nativeDevice) {
      return
    }

    try {
      const HID = NodeHidChannel.loadNodeHid()

      const nativeDevice = new HID.HID(this.path)
      this.nativeDevice = nativeDevice

      console.log(`[NodeHID] 设备已打开: ${this.path}`)

      nativeDevice.on('error', (err: any) => {
        console.error(`[NodeHID] 设备错误 (${this.path}):`, err)
        this.close()
      })
    } catch (error) {
      console.error(
        `[NodeHID] 打开设备失败: ${this.path} ` +
          `(VID: ${this._deviceInfo?.vendorId}, PID: ${this._deviceInfo?.productId})`,
        error,
      )
      if (error instanceof DeviceError) {
        throw error
      }
      throw new DeviceError(DeviceErrorCode.OPEN_FAILED, `打开设备失败: ${this.path}`, error)
    }
  }

  public async close(): Promise<void> {
    if (this.nativeDevice) {
      try {
        this.nativeDevice.close()
      } catch (error) {
        // 关闭失败通常忽略，但记录日志
        console.warn('[NodeHID] 关闭设备时发生错误:', error)
      }
      this.nativeDevice = null
    }
  }

  public async sendOutputReport(reportId: number, data: Uint8Array): Promise<void> {
    if (!this.nativeDevice) {
      throw new DeviceError(DeviceErrorCode.NOT_CONNECTED, '设备未连接')
    }

    try {
      // node-hid write 期望格式为 [reportId, ...data]
      // 或者如果 reportId 为 0 则只传 data (但通常是 [id, ...])
      // 这里传入的 data 是负载数据。
      const buffer = [reportId, ...Array.from(data)]
      this.nativeDevice.write(buffer)
    } catch (error) {
      console.error('[NodeHID] 发送报告失败:', error)
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '发送报告失败', error)
    }
  }

  public async sendFeatureReport(reportId: number, data: Uint8Array): Promise<void> {
    if (!this.nativeDevice) {
      throw new DeviceError(DeviceErrorCode.NOT_CONNECTED, '设备未连接')
    }

    try {
      // node-hid sendFeatureReport 期望格式为 [reportId, ...data]
      const buffer = [reportId, ...Array.from(data)]
      this.nativeDevice.sendFeatureReport(buffer)
    } catch (error) {
      console.error('[NodeHID] 发送 Feature Report 失败:', error)
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '发送 Feature Report 失败', error)
    }
  }

  public async getFeatureReport(reportId: number): Promise<DataView> {
    if (!this.nativeDevice) {
      throw new DeviceError(DeviceErrorCode.NOT_CONNECTED, '设备未连接')
    }

    try {
      // getFeatureReport 返回 number[] (byte array)
      const buffer = this.nativeDevice.getFeatureReport(reportId, 65) // 假设最大长度，或者需要指定长度
      // node-hid 返回的第一个字节通常是 reportId (取决于 OS/driver)，但也可能是数据
      // 需要确认 node-hid 行为。通常 getFeatureReport 返回 [reportId, data...] 或 [data...]
      // 官方文档：Returns an array of numbers.
      // 假设返回的是包含 Report ID 的数组
      // 如果需要 DataView，我们转一下

      // 注意：这里可能需要根据 reportId 过滤掉第一个字节？
      // 暂时保持原样，假设返回的数据可以直接使用
      return new DataView(new Uint8Array(buffer).buffer)
    } catch (error) {
      console.error('[NodeHID] 获取 Feature Report 失败:', error)
      throw new DeviceError(DeviceErrorCode.READ_FAILED, '获取 Feature Report 失败', error)
    }
  }

  public onInputReport(callback: (reportId: number, data: DataView) => void): void {
    if (!this.nativeDevice) return

    // 关键：注册前先移除同一回调，避免重复触发 data 事件。
    this.offInputReport(callback)

    const listener = (data: Buffer) => {
      // node-hid 'data' 事件通常不包含 reportId 作为第一个字节（如果 usesHIDDescriptor 为 true?）
      // 或者它就是原始 buffer。
      // 对于 HID 设备，通常第一个字节是 Report ID。
      // 需要确认 node-hid 的行为。
      // 假设 data[0] 是 reportId
      if (data.length > 0) {
        const reportId = data[0]
        const payload = data.subarray(1) // 去掉 Report ID
        // 将 Buffer 转为 DataView
        // 注意：Buffer 的 underlying buffer 可能包含 offset，所以要小心
        const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength)
        callback(reportId, view)
      }
    }

    this.listeners.set(callback, listener)
    this.nativeDevice.on('data', listener)
  }

  public offInputReport(callback: (reportId: number, data: DataView) => void): void {
    if (!this.nativeDevice) return

    const listener = this.listeners.get(callback)
    if (listener) {
      this.nativeDevice.removeListener('data', listener)
      this.listeners.delete(callback)
    }
  }
}
